import { assign, createMachine, send, sendParent } from 'xstate';

import { GetReferralCodeResponseType } from '@nw/shared/data-access';
import {
  COUPON_CODE,
  ERROR,
  getQueryParamValue,
  IDLE,
  INVITE_CODE,
  LOADING,
  SUCCESS
} from '@nw/shared/utils';

import {
  PRODUCT_MACHINE_ID,
  productMachineActions,
  productMachineEvents,
  productMachineGuards,
  productMachineServices,
  productsMachineStates
} from '../../constants/productMachineConstants';
import {
  ProductMachineContextType,
  ProductMachineEventTypes
} from '../../types/productMachineTypes';

const states = productsMachineStates;
const services = productMachineServices;
const actions = productMachineActions;
const events = productMachineEvents;
const guards = productMachineGuards;

export const ProductsMachine = (
  productId: string,
  sectionName: string,
  userApiInviteCode: string,
  initialState?: string
) => {
  return createMachine<ProductMachineContextType, ProductMachineEventTypes>({
    id: PRODUCT_MACHINE_ID,
    context: {
      productCode: productId,
      productDetail: null,
      inviteCode:
        userApiInviteCode === getQueryParamValue(INVITE_CODE)
          ? ''
          : getQueryParamValue(INVITE_CODE) || '',
      couponCode: getQueryParamValue(COUPON_CODE) || '',
      values: {},
      paymentOrderDetails: null,
      paymentServiceInfo: null,
      userProfileDetails: null,
      paymentAuthCode: '',
      shouldModifyOrderDetails: false,
      shouldShowProductSuccessPage: true,
      errorMessages: {
        referralCodeErrorMessage: '',
        couponCodeErrorMessage: '',
        createPaymentOrderAndGetDetailsErrorMessage: '',
        configApiErrorMessage: '',
        getSelectedProductPaymentServiceErrorMessage: '',
        executeSectionActionErrorMessage: '',
        bootstrapExternalManagedErrorMessage: '',
        refreshExternalManagedPaymentDetailsErrorMessage: '',
        submitExternalManagedPaymentProofErrorMessage: ''
      },
      apiStatus: {
        configApiStatus: IDLE,
        submitProductOrCreatePaymentApiStatus: IDLE,
        getSelectedProductPaymentServiceApiStatus: IDLE,
        getReferralCodeDetailApiStatus: IDLE,
        getCouponCodeDetailApiStatus: IDLE,
        executeSectionActionApiStatus: IDLE,
        getInstallmentOrderPaymentDetailsApiStatus: IDLE,
        bootstrapExternalManagedApiStatus: IDLE,
        refreshExternalManagedPaymentDetailsApiStatus: IDLE,
        submitExternalManagedPaymentProofApiStatus: IDLE
      },
      userOrdersDetails: null,
      applicationSectionVisitDetails: null,
      referralCodes: {},
      installmentOrderSnapshot: null,
      installmentPaymentHistory: [],
      externalManagedServiceDetails: null,
      externalManagedPaymentSummary: null,
      externalManagedOrderId: ''
    },
    initial: initialState ?? states.getProductReferralCodesState,
    on: {
      [events.getReferralCodeDetail]: {
        target: states.getReferralCodeDetailState
      },
      [events.getCouponCodeDetails]: {
        target: states.getCouponCodeDetailState
      },
      [events.resetErrorMessageAndApiStatus]: {
        actions: [actions.resetErrorMessageAndApiStatus]
      },
      [events.onClickContinueToPay]: [
        {
          actions: [actions.updateParentContextValue],
          target: states.createPaymentOrderAndGetDetailState,
          cond: guards.isProductAmountToBePaidZero
        },
        {
          actions: [actions.updateParentContextValue],
          target: states.getSelectedProductPaymentServiceState
        }
      ],
      [events.cancelPaymentOrder]: {
        target: states.cancelPaymentOrderState
      },
      [events.getPaymentStatus]: {
        target: states.getOrderStatusState
      },
      [events.executeSectionAction]: [
        {
          actions: [actions.updateParentContextValue],
          target: states.createPaymentOrderAndGetDetailState,
          cond: guards.isProductAmountToBePaidZero
        },
        {
          actions: [actions.updateParentContextValue],
          target: states.executeSectionActionState
        }
      ],
      [events.modifyOrderDetails]: {
        actions: [
          assign((context: ProductMachineContextType) => {
            return {
              shouldModifyOrderDetails: true,
              apiStatus: {
                ...context.apiStatus,
                submitProductOrCreatePaymentApiStatus: IDLE
              },
              errorMessages: {
                ...context.errorMessages,
                createPaymentOrderAndGetDetailsErrorMessage: ''
              }
            };
          })
        ],
        target: states.getProductReferralCodesState
      },
      [events.getProductDetails]: {
        actions: [
          assign((context: ProductMachineContextType) => {
            return {
              shouldShowProductSuccessPage: false
            };
          })
        ],
        target: states.getProductReferralCodesState
      },
      [events.getInstallmentOrderPaymentDetails]: {
        target: states.getInstallmentOrderPaymentDetailsState
      },
      [events.getExternalManagedPaymentDetails]: {
        target: states.refreshExternalManagedPaymentDetailsState
      },
      [events.submitExternalManagedPaymentProof]: {
        actions: [actions.updateParentContextValue],
        target: states.submitExternalManagedPaymentProofState
      }
    },
    states: {
      [states.getProductReferralCodesState]: {
        entry: [
          assign((context: ProductMachineContextType) => {
            return {
              apiStatus: {
                ...context.apiStatus,
                configApiStatus: LOADING
              }
            };
          })
        ],
        invoke: {
          src: services.getProductReferralCodes,
          onDone: [
            {
              cond: guards.shouldTrackSecondaryReferralAttempt,
              actions: [actions.onSuccessGetProductReferralCodes],
              target: states.trackReferralAttemptState
            },
            {
              actions: [actions.onSuccessGetProductReferralCodes],
              target: states.getProductConfigState
            }
          ],
          onError: [
            {
              cond: guards.isLicenceCodeCarryForwardConfigNotFound,
              actions: [
                assign((context: ProductMachineContextType) => {
                  return {
                    inviteCode: context.inviteCode,
                    apiStatus: {
                      ...context.apiStatus,
                      configApiStatus: ERROR
                    }
                  };
                })
              ],
              target: states.getProductConfigState
            },
            {
              actions: [
                assign((context: ProductMachineContextType) => {
                  return {
                    apiStatus: {
                      ...context.apiStatus,
                      configApiStatus: ERROR
                    }
                  };
                })
              ]
            }
          ]
        }
      },
      [states.trackReferralAttemptState]: {
        entry: [
          assign((context: ProductMachineContextType) => {
            return {
              apiStatus: {
                ...context.apiStatus,
                configApiStatus: LOADING
              }
            };
          })
        ],
        invoke: {
          src: services.trackReferralAttempt,
          onDone: {
            actions: [
              assign((context: ProductMachineContextType) => {
                return {
                  apiStatus: {
                    ...context.apiStatus,
                    configApiStatus: SUCCESS
                  }
                };
              })
            ],
            target: states.getProductConfigState
          },
          onError: {
            actions: [
              assign((context: ProductMachineContextType) => {
                return {
                  apiStatus: {
                    ...context.apiStatus,
                    configApiStatus: SUCCESS
                  }
                };
              }),
              actions.onErrorTrackReferralAttemptLogEntry
            ],
            target: states.getProductConfigState
          }
        }
      },
      [states.getProductConfigState]: {
        entry: [
          assign((context: ProductMachineContextType, event, meta) => {
            return {
              apiStatus: {
                ...context.apiStatus,
                configApiStatus: LOADING
              }
            };
          })
        ],
        invoke: {
          src: services.getProductConfig,
          onDone: [
            {
              cond: guards.isNiatEnrollmentExternalManagedFromProductConfig,
              actions: [actions.onSuccessGetProductConfig],
              target: states.bootstrapExternalManagedState
            },
            {
              cond: guards.isProductFeeBasedOnTimer,
              actions: [
                actions.onSuccessGetProductConfig,
                assign((context: ProductMachineContextType) => {
                  return {
                    apiStatus: {
                      ...context.apiStatus,
                      configApiStatus: SUCCESS
                    }
                  };
                })
              ],
              target: states.getOrCreateApplicationSectionVisitState
            },
            {
              actions: [
                actions.onSuccessGetProductConfig,
                assign((context: ProductMachineContextType) => {
                  return {
                    apiStatus: {
                      ...context.apiStatus,
                      configApiStatus: SUCCESS
                    }
                  };
                })
              ]
            }
          ],
          onError: [
            {
              actions: [
                assign((context: ProductMachineContextType) => {
                  return {
                    apiStatus: {
                      ...context.apiStatus,
                      configApiStatus: ERROR
                    }
                  };
                })
              ]
            }
          ]
        }
      },
      [states.getOrCreateApplicationSectionVisitState]: {
        entry: [
          assign((context: ProductMachineContextType) => {
            return {
              apiStatus: {
                ...context.apiStatus,
                configApiStatus: LOADING
              }
            };
          })
        ],
        invoke: {
          src: services.getOrCreateApplicationSectionVisit,
          onDone: {
            actions: [
              actions.onSuccessGetOrCreateApplicationSectionVisit,
              assign((context: ProductMachineContextType) => {
                return {
                  apiStatus: {
                    ...context.apiStatus,
                    configApiStatus: SUCCESS
                  }
                };
              })
            ]
          },
          onError: {
            actions: [
              assign((context: ProductMachineContextType) => {
                return {
                  apiStatus: {
                    ...context.apiStatus,
                    configApiStatus: ERROR
                  }
                };
              })
            ]
          }
        }
      },
      [states.getUserOrdersDetailsState]: {
        entry: [
          sendParent((context: ProductMachineContextType) => {
            return {
              type: 'UPDATE_SHOULD_DISABLE_ALL_SECTIONS_TABS',
              value: true
            };
          }),
          assign((context: ProductMachineContextType, event, meta) => {
            return {
              apiStatus: {
                ...context.apiStatus,
                configApiStatus: LOADING
              }
            };
          })
        ],
        invoke: {
          src: services.getUserOrdersDetails,
          onDone: {
            actions: [
              actions.onSuccessGettingUserOrdersDetails,
              sendParent((context: ProductMachineContextType) => {
                return {
                  type: 'UPDATE_SHOULD_DISABLE_ALL_SECTIONS_TABS',
                  value: false
                };
              }),
              assign((context: ProductMachineContextType) => {
                return {
                  apiStatus: {
                    ...context.apiStatus,
                    configApiStatus: SUCCESS
                  }
                };
              })
            ]
          },
          onError: {
            actions: [
              assign((context: ProductMachineContextType) => {
                return {
                  apiStatus: {
                    ...context.apiStatus,
                    configApiStatus: ERROR
                  }
                };
              })
            ]
          }
        }
      },
      [states.getReferralCodeDetailState]: {
        entry: [
          assign((context: ProductMachineContextType, event: any) => {
            return {
              apiStatus: {
                ...context.apiStatus,
                getReferralCodeDetailApiStatus: LOADING
              }
            };
          })
        ],
        invoke: {
          src: services.getReferralCodeDetail,
          onDone: {
            actions: [actions.onSuccessGetReferralCodeDetail]
          },
          onError: [
            {
              cond: guards.isLicenceCodeCarryForwardConfigNotFound,
              actions: [actions.onSuccessGetReferralCodeDetail]
            },
            {
              actions: [actions.onErrorGetReferralCodeDetail]
            }
          ]
        }
      },
      [states.getCouponCodeDetailState]: {
        entry: [
          assign((context, _) => {
            return {
              apiStatus: {
                ...context.apiStatus,
                getCouponCodeDetailApiStatus: LOADING
              }
            };
          })
        ],
        invoke: {
          src: services.getReferralCodeDetail,
          onDone: {
            actions: [actions.onSuccessGetCouponCodeDetail]
          },
          onError: {
            actions: [actions.onErrorGetCouponCodeDetail]
          }
        }
      },
      [states.executeSectionActionState]: {
        entry: [
          assign((context: ProductMachineContextType) => {
            return {
              apiStatus: {
                ...context.apiStatus,
                executeSectionActionApiStatus: LOADING
              }
            };
          })
        ],
        invoke: {
          src: services.executeSectionAction,
          onDone: {
            actions: [
              assign((context: ProductMachineContextType) => {
                return {
                  apiStatus: {
                    ...context.apiStatus,
                    executeSectionActionApiStatus: SUCCESS
                  }
                };
              })
            ],
            target: states.getSelectedProductPaymentServiceState
          },
          onError: [
            {
              cond: guards.isUserAlreadyHaveAccessToCoupon,
              target: states.getSelectedProductPaymentServiceState,
              actions: [
                assign((context: ProductMachineContextType) => {
                  return {
                    apiStatus: {
                      ...context.apiStatus,
                      executeSectionActionApiStatus: SUCCESS
                    }
                  };
                })
              ]
            },
            {
              actions: [
                assign((context: ProductMachineContextType) => {
                  return {
                    apiStatus: {
                      ...context.apiStatus,
                      executeSectionActionApiStatus: ERROR
                    }
                  };
                })
              ]
            }
          ]
        }
      },
      [states.getSelectedProductPaymentServiceState]: {
        entry: [
          assign((context: ProductMachineContextType) => {
            return {
              apiStatus: {
                ...context.apiStatus,
                getSelectedProductPaymentServiceApiStatus: LOADING
              }
            };
          })
        ],

        invoke: {
          src: services.getSelectedProductPaymentService,
          onDone: {
            actions: [
              actions.onSuccessGetSelectedProductPaymentServices,
              assign((context: ProductMachineContextType) => {
                return {
                  apiStatus: {
                    ...context.apiStatus,
                    getSelectedProductPaymentServiceApiStatus: SUCCESS
                  }
                };
              })
            ],
            target: states.createPaymentOrderAndGetDetailState
          },
          onError: {
            actions: [
              assign((context: ProductMachineContextType) => {
                return {
                  apiStatus: {
                    ...context.apiStatus,
                    getSelectedProductPaymentServiceApiStatus: ERROR
                  }
                };
              })
            ]
          }
        }
      },
      [states.createPaymentOrderAndGetDetailState]: {
        entry: [
          assign((context: ProductMachineContextType) => {
            return {
              apiStatus: {
                ...context.apiStatus,
                submitProductOrCreatePaymentApiStatus: LOADING
              }
            };
          })
        ],
        invoke: {
          src: services.createPaymentOrderAndGetDetail,
          onDone: [
            {
              actions: [actions.onSuccessCreatePaymentOrderAndGetDetail],
              target: states.getOrderStatusState,
              cond: guards.isProductAmountToBePaidZero
            },
            {
              actions: [
                actions.onSuccessCreatePaymentOrderAndGetDetail,
                assign((context: ProductMachineContextType) => {
                  return {
                    apiStatus: {
                      ...context.apiStatus,
                      submitProductOrCreatePaymentApiStatus: SUCCESS
                    }
                  };
                })
              ]
            }
          ],
          onError: [
            {
              cond: guards.isProductFeeBasedOnTimerAndCouponExpired,
              target: states.newOfferUnlockedState,
              actions: [actions.onErrorCreatePaymentOrderAndGetDetail]
            },
            {
              actions: [actions.onErrorCreatePaymentOrderAndGetDetail]
            }
          ]
        }
      },
      [states.newOfferUnlockedState]: {
        on: {
          [events.onClickContinueToPay]: [
            {
              actions: [actions.updateParentContextValue],
              target: states.createPaymentOrderAndGetDetailState,
              cond: guards.isProductAmountToBePaidZero
            },
            {
              actions: [actions.updateParentContextValue],
              target: states.getSelectedProductPaymentServiceState
            }
          ]
        }
      },
      [states.getOrderStatusState]: {
        invoke: {
          src: services.getOrderStatus,
          onDone: {
            actions: [
              actions.onSuccessGettingPaymentStatus,
              sendParent((context: ProductMachineContextType) => {
                return {
                  type: 'UPDATE_SECTION_PERCENTAGE',
                  data: {
                    current_section: sectionName,
                    completion_percentage: 100
                  }
                };
              })
            ]
          },
          onError: {
            actions: [actions.onErrorCreatePaymentOrderAndGetDetail]
          }
        }
      },

      [states.cancelPaymentOrderState]: {
        invoke: {
          src: services.cancelPaymentOrder,
          onDone: {
            actions: [actions.onSuccessCancelPaymentOrder]
          },
          onError: {}
        }
      },
      [states.getInstallmentOrderPaymentDetailsState]: {
        entry: [
          assign((context: ProductMachineContextType) => {
            return {
              apiStatus: {
                ...context.apiStatus,
                getInstallmentOrderPaymentDetailsApiStatus: LOADING
              }
            };
          })
        ],
        invoke: {
          src: services.getInstallmentOrderPaymentDetails,
          onDone: {
            actions: [
              actions.onSuccessGetInstallmentOrderPaymentDetails,
              assign((context: ProductMachineContextType) => {
                return {
                  apiStatus: {
                    ...context.apiStatus,
                    getInstallmentOrderPaymentDetailsApiStatus: SUCCESS
                  }
                };
              })
            ]
          },
          onError: {
            actions: [
              assign((context: ProductMachineContextType) => {
                return {
                  apiStatus: {
                    ...context.apiStatus,
                    getInstallmentOrderPaymentDetailsApiStatus: ERROR
                  }
                };
              })
            ]
          }
        }
      },
      [states.bootstrapExternalManagedState]: {
        entry: [
          assign((context: ProductMachineContextType) => {
            return {
              apiStatus: {
                ...context.apiStatus,
                bootstrapExternalManagedApiStatus: LOADING
              },
              errorMessages: {
                ...context.errorMessages,
                bootstrapExternalManagedErrorMessage: ''
              }
            };
          })
        ],
        invoke: {
          src: services.bootstrapExternalManagedEnrollment,
          onDone: {
            actions: [
              actions.onSuccessBootstrapExternalManaged,
              assign((context: ProductMachineContextType) => {
                return {
                  apiStatus: {
                    ...context.apiStatus,
                    bootstrapExternalManagedApiStatus: SUCCESS
                  }
                };
              })
            ]
          },
          onError: {
            actions: [
              actions.onErrorBootstrapExternalManaged,
              assign((context: ProductMachineContextType) => {
                return {
                  apiStatus: {
                    ...context.apiStatus,
                    bootstrapExternalManagedApiStatus: ERROR
                  }
                };
              })
            ]
          }
        }
      },
      [states.refreshExternalManagedPaymentDetailsState]: {
        entry: [
          assign((context: ProductMachineContextType) => {
            return {
              apiStatus: {
                ...context.apiStatus,
                refreshExternalManagedPaymentDetailsApiStatus: LOADING
              },
              errorMessages: {
                ...context.errorMessages,
                refreshExternalManagedPaymentDetailsErrorMessage: ''
              }
            };
          })
        ],
        invoke: {
          src: services.refreshExternalManagedPaymentDetails,
          onDone: {
            actions: [
              actions.onSuccessRefreshExternalManagedPaymentDetails,
              assign((context: ProductMachineContextType) => {
                return {
                  apiStatus: {
                    ...context.apiStatus,
                    refreshExternalManagedPaymentDetailsApiStatus: SUCCESS
                  }
                };
              })
            ]
          },
          onError: {
            actions: [
              actions.onErrorRefreshExternalManagedPaymentDetails,
              assign((context: ProductMachineContextType) => {
                return {
                  apiStatus: {
                    ...context.apiStatus,
                    refreshExternalManagedPaymentDetailsApiStatus: ERROR
                  }
                };
              })
            ]
          }
        }
      },
      [states.submitExternalManagedPaymentProofState]: {
        entry: [
          assign((context: ProductMachineContextType) => {
            return {
              apiStatus: {
                ...context.apiStatus,
                submitExternalManagedPaymentProofApiStatus: LOADING
              },
              errorMessages: {
                ...context.errorMessages,
                submitExternalManagedPaymentProofErrorMessage: ''
              }
            };
          })
        ],
        invoke: {
          src: services.submitExternalManagedPaymentProof,
          onDone: [
            {
              cond: (context, event: any) => {
                const totalVerifiedLeast = Number(
                  event?.data?.paymentSummary?.total_verified_amount_in_least_subunit ?? 0
                );
                const totalRequiredRupee = Number(
                  context.productDetail?.enrollmentInstallmentMeta?.totalAmountToBePaid ?? 0
                );
                const totalRequiredLeast = totalRequiredRupee * 100;
                return totalRequiredLeast > 0 && totalVerifiedLeast >= totalRequiredLeast;
              },
              actions: [
                actions.onSuccessSubmitExternalManagedPaymentProof,
                assign((context: ProductMachineContextType) => {
                  return {
                    apiStatus: {
                      ...context.apiStatus,
                      submitExternalManagedPaymentProofApiStatus: SUCCESS
                    }
                  };
                }),
                sendParent((_context, event: any) => {
                  return {
                    type: 'UPDATE_SECTION_PERCENTAGE',
                    data: {
                      current_section: sectionName,
                      completion_percentage: 100
                    }
                  };
                })
              ]
            },
            {
              actions: [
                actions.onSuccessSubmitExternalManagedPaymentProof,
                assign((context: ProductMachineContextType) => {
                  return {
                    apiStatus: {
                      ...context.apiStatus,
                      submitExternalManagedPaymentProofApiStatus: SUCCESS
                    }
                  };
                })
              ]
            }
          ],
          onError: {
            actions: [
              actions.onErrorSubmitExternalManagedPaymentProof,
              assign((context: ProductMachineContextType) => {
                return {
                  apiStatus: {
                    ...context.apiStatus,
                    submitExternalManagedPaymentProofApiStatus: ERROR
                  }
                };
              })
            ]
          }
        }
      }
    }
  });
};
